/**
 * MATransactionsView - NVIDIA Comparable M&A Transactions page view
 *
 * Displays comparable M&A transactions analysis
 *
 * TODO: Transaction data structure to be implemented
 * TODO: DataService integration for loading/saving transactions
 */

import { BaseView } from './BaseView';
import { DataService } from '../services/DataService';
import { StorageService } from '../services/StorageService';
import type { MATransaction, MACompanyEntity } from '@/types';
import * as XLSX from 'xlsx';

export class MATransactionsView extends BaseView {
	private dataService: DataService;
	private storageService: StorageService;
	private selectedTransactions: MATransaction[] = [];

	constructor() {
		super();
		this.dataService = new DataService();
		this.storageService = new StorageService();
	}

	/**
	 * Render the M&A transactions view content
	 */
	render(): string {
		return `
			<div class="company-header">
				<div class="company-name"><a href="/profile">NVIDIA Corporation (NasdaqGS:NVDA)</a> Comparable M&A Transactions</div>
			</div>

			<div class="button-row">
				<div class="tool-button download-button">
					<img src="../img/icon1.png" alt="Download M&A Transactions" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Download M&A Transactions</span>
				</div>
				<div class="tool-button print-button">
					<i data-lucide="printer" style="width: 16px; height: 16px; margin-right: 3px;"></i>
					<span class="button-text">Print</span>
				</div>
			</div>

			<div class="ma-transactions-section">
				<!-- Dark gray section header -->
				<div class="ma-section-header">
					<span class="ma-section-title">Comparable M&A transactions</span>
				</div>

				<!-- Add Transaction button -->
				<div class="ma-button-container">
					<button class="add-transaction-btn" id="add-transaction-btn">Add Transaction</button>
				</div>

				<!-- Empty state message -->
				<div class="ma-empty-state" id="ma-empty-state">
					There are no comparable Transactions
				</div>

				<!-- Main Transactions Table -->
				<div class="ma-transactions-table-container" id="ma-transactions-table-container" style="display: none;">
					<table class="ma-transactions-table" cellspacing="0" cellpadding="2" rules="all" bordercolor="#d8dde1" border="1" id="ma-transactions-main-table">
						<thead>
							<tr class="ma-table-header">
								<th align="center" style="width: 5%;">Delete?</th>
								<th align="left" style="width: 10%;">Announced Date</th>
								<th align="left">Target</th>
								<th align="left" style="width: 17%;">Buyer/Investor</th>
								<th align="left" style="width: 17%;">Seller</th>
								<th align="right" style="width: 8%;">Target TEV(USD mm)</th>
								<th align="right" style="width: 6%;">Size(USD mm)</th>
								<th align="right" style="width: 11%;">Implied TEV/LTM Revenue</th>
								<th align="right" style="width: 11%;">Implied TEV/LTM EBITDA</th>
							</tr>
						</thead>
						<tbody id="ma-transactions-tbody"></tbody>
					</table>
				</div>

				<!-- Transaction Comp Set Save/Restore Section -->
				<div class="ma-comp-set-section" id="ma-comp-set-section" style="display: none;">
					<div class="ma-comp-set-container">
						<label class="ma-comp-set-label">Create Transaction Comp Set</label>
						<input type="text" id="comp-set-name-input" class="ma-comp-set-input" placeholder="Enter comp set name...">
						<button class="ma-comp-set-save-btn" id="comp-set-save-btn" disabled>Save</button>
						<button class="ma-comp-set-restore-btn" id="comp-set-restore-btn">Restore</button>
					</div>
				</div>
			</div>

			<style>
				.ma-transactions-section {
					background: white;
					padding: 0;
					margin-top: 0;
				}

				.ma-section-header {
					background-color: #4a4a4a;
					width: 100%;
					padding: 8px 12px;
					border-bottom: 1px solid #333;
				}

				.ma-section-title {
					color: white;
					font-weight: bold;
					font-size: 12px;
					text-align: left;
				}

				.ma-button-container {
					padding: 8px 12px;
					background-color: #f0f0f0;
					border-bottom: 1px solid #ccc;
				}

				.add-transaction-btn {
					background-color: #f9f9f9;
					border: 1px solid #000;
					border-radius: 0;
					padding: 4px 8px;
					font-size: 11px;
					color: #000;
					cursor: pointer;
					font-family: Arial, sans-serif;
				}

				.add-transaction-btn:hover {
					background-color: #e6e6e6;
				}

				.ma-empty-state {
					padding: 20px 12px;
					font-size: 12px;
					color: #000;
					font-weight: normal;
					font-family: Arial, sans-serif;
				}

				.ma-transactions-list {
					padding: 12px;
				}

				/* Transaction Search Modal Styles */
				.ma-modal-overlay {
					position: fixed;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background-color: rgba(0, 0, 0, 0.5);
					display: flex;
					justify-content: center;
					align-items: center;
					z-index: 10000;
				}

				.ma-modal-container {
					background-color: #efefef;
					width: 800px;
					max-width: 95%;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
					display: flex;
					flex-direction: column;
					max-height: 90vh;
					overflow: hidden;
				}

				.ma-modal-header {
					background: linear-gradient(to bottom, #16497d, #0d2a48);
					color: white;
					padding: 2px 4px;
					display: flex;
					justify-content: space-between;
					align-items: center;
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 11px;
					border: 2px solid #16497d;
				}

				.ma-modal-close {
					background: none;
					border: none;
					color: white;
					font-size: 18px;
					cursor: pointer;
					padding: 0;
					width: 20px;
					height: 20px;
					line-height: 18px;
					text-align: center;
				}

				.ma-modal-close:hover {
					opacity: 0.8;
				}

				.ma-modal-body {
					background-color: white;
					padding: 0;
					overflow-y: auto;
					max-height: calc(90vh - 30px);
				}

				.ma-modal-section-title {
					color: #222222;
					font-weight: bold;
					font-size: 11px;
					font-family: Verdana, sans-serif;
					padding: 2px;
					margin: 0;
					background: white;
					border-bottom: 1px solid #d8dde1;
				}

				.ma-modal-content {
					background-color: #efefef;
					padding: 5px;
					border: 1px solid #d8dde1;
				}

				/* Search By Section */
				.ma-search-by-section {
					display: flex;
					align-items: center;
					gap: 8px;
					margin-bottom: 8px;
					background: #efefef;
					padding: 5px;
				}

				.ma-search-label {
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 10px;
					color: #48535b;
					min-width: 102px;
				}

				.ma-radio-group {
					display: flex;
					gap: 16px;
					align-items: center;
				}

				.ma-radio-option {
					display: flex;
					align-items: center;
					gap: 4px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: #000;
					cursor: pointer;
				}

				.ma-radio-option input[type="radio"] {
					width: 13px;
					height: 13px;
					margin: 0;
					cursor: pointer;
					accent-color: #0075ff;
				}

				/* Company Input Section */
				.ma-company-input-section {
					display: flex;
					gap: 8px;
					align-items: flex-start;
					margin-bottom: 8px;
					background: #efefef;
					padding: 2px;
				}

				.ma-company-label {
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 10px;
					color: #48535b;
					min-width: 102px;
					line-height: 1.2;
				}

				.ma-input-group {
					display: flex;
					gap: 14px;
					align-items: center;
					flex: 1;
				}

				.ma-company-input {
					width: 224px;
					height: 17px;
					border: 1px solid #767676;
					border-radius: 2.5px;
					padding: 2px;
					font-family: Verdana, sans-serif;
					font-size: 11px;
					color: #666666;
				}

				.ma-go-button {
					background: #efefef;
					border: 2px solid #000;
					padding: 2px 7px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: #000;
					cursor: pointer;
					height: 20px;
					min-width: 28px;
				}

				.ma-go-button:hover {
					background: #e0e0e0;
				}

				/* Dual List Section */
				.ma-dual-list-section {
					display: flex;
					gap: 8px;
					align-items: flex-start;
					margin-bottom: 12px;
					background: #efefef;
					padding: 2px;
				}

				.ma-list-container {
					flex: 1;
					display: flex;
					flex-direction: column;
					gap: 3px;
				}

				.ma-list-header {
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 10px;
					color: #48535b;
					padding: 1px;
				}

				.ma-list-select {
					width: 100%;
					min-height: 115px;
					border: 1px solid #767676;
					border-radius: 2px;
					background: white;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					padding: 1px;
				}

				.ma-arrow-buttons {
					display: flex;
					flex-direction: column;
					gap: 2px;
					padding-top: 16px;
					min-width: 28px;
				}

				.ma-arrow-btn {
					background: #efefef;
					border: 2px solid #000;
					width: 28px;
					height: 20px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: #000;
					cursor: pointer;
					text-align: center;
					padding: 2px 7px;
					line-height: 1;
				}

				.ma-arrow-btn:hover {
					background: #e0e0e0;
				}

				/* Date Section */
				.ma-date-section {
					display: flex;
					flex-direction: column;
					gap: 10px;
					margin-bottom: 12px;
					padding: 2px;
					justify-content: center;
					align-items: center;
				}

				.ma-date-row {
					display: flex;
					gap: 4px;
					align-items: center;
				}

				.ma-date-label {
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 10px;
					color: #000;
					min-width: 130px;
					text-align: right;
				}

				.ma-date-input-wrapper {
					position: relative;
				}

				.ma-date-input {
					width: 94px;
					height: 17px;
					border: 1px solid #767676;
					border-radius: 2.5px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					padding: 2px;
				}

				/* Action Buttons */
				.ma-actions {
					display: flex;
					gap: 0;
					justify-content: center;
					padding: 2px;
				}

				.ma-view-transactions-btn {
					background: #e0e0e0;
					border: 2px solid rgba(118, 118, 118, 0.3);
					padding: 4px 7px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: rgba(16, 16, 16, 0.3);
					cursor: not-allowed;
					height: 20px;
				}

				.ma-view-transactions-btn:enabled {
					background: #efefef;
					border: 2px solid #000;
					color: #000;
					cursor: pointer;
				}

				.ma-view-transactions-btn:enabled:hover {
					background: #e0e0e0;
				}

				.ma-reset-btn {
					background: #efefef;
					border: 2px solid #000;
					padding: 4px 7px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: #000;
					cursor: pointer;
					height: 20px;
				}

				.ma-reset-btn:hover {
					background: #e0e0e0;
				}

				/* Main Transactions Table Styles */
				.ma-transactions-table-container {
					padding: 0 12px 12px 12px;
				}

				.ma-transactions-table {
					width: 100%;
					border-collapse: collapse;
					font-family: Verdana, sans-serif;
					font-size: 11px;
					background: white;
				}

				.ma-transactions-table th {
					background: #e6e8ea;
					border: 1px solid #d8dde1;
					padding: 4px 8px;
					font-weight: bold;
					color: #333;
					font-size: 10px;
				}

				.ma-transactions-table td {
					border: 1px solid #d8dde1;
					padding: 4px 8px;
					font-size: 11px;
					color: #000;
				}

				.ma-transactions-table tr:nth-child(even) {
					background: #f5f5f5;
				}

				.ma-transactions-table tr:hover {
					background: #e8f4fd;
				}

				.ma-delete-icon {
					width: 10px;
					height: 10px;
					cursor: pointer;
				}

				.ma-delete-icon:hover {
					opacity: 0.7;
				}

				.ma-add-icon {
					width: 10px;
					height: 10px;
					cursor: pointer;
				}

				.ma-add-icon:hover {
					opacity: 0.7;
				}

				.ma-add-icon.disabled {
					opacity: 0.3;
					cursor: not-allowed;
				}

				/* Modal Search Results Table */
				.ma-modal-results-table-container {
					margin-top: 12px;
					max-height: 300px;
					overflow-y: auto;
					border: 1px solid #d8dde1;
				}

				.ma-modal-results-table {
					width: 100%;
					border-collapse: collapse;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					background: white;
				}

				.ma-modal-results-table th {
					background: #e6e8ea;
					border: 1px solid #d8dde1;
					padding: 3px 6px;
					font-weight: bold;
					color: #333;
					position: sticky;
					top: 0;
					z-index: 1;
				}

				.ma-modal-results-table td {
					border: 1px solid #d8dde1;
					padding: 3px 6px;
					font-size: 10px;
					color: #000;
				}

				.ma-modal-results-table tr:nth-child(even) {
					background: #f5f5f5;
				}

				.ma-modal-results-table tr:hover {
					background: #e8f4fd;
				}

				/* Transaction Comp Set Save/Restore Section */
				.ma-comp-set-section {
					padding: 12px;
					display: flex;
					justify-content: flex-end !important;
					width: 100%;
				}

				.ma-comp-set-container {
					display: flex;
					align-items: center;
					gap: 8px;
				}

				.ma-comp-set-label {
					font-family: Verdana, sans-serif;
					font-weight: bold;
					font-size: 11px;
					color: #000;
				}

				.ma-comp-set-input {
					width: 200px;
					height: 17px;
					border: 1px solid #767676;
					border-radius: 2.5px;
					padding: 2px;
					font-family: Verdana, sans-serif;
					font-size: 11px;
					color: #000;
				}

				.ma-comp-set-save-btn,
				.ma-comp-set-restore-btn {
					background: #efefef;
					border: 2px solid #000;
					padding: 2px 7px;
					font-family: Verdana, sans-serif;
					font-size: 10px;
					color: #000;
					cursor: pointer;
					height: 20px;
					min-width: 50px;
				}

				.ma-comp-set-save-btn:hover:not(:disabled),
				.ma-comp-set-restore-btn:hover {
					background: #e0e0e0;
				}

				.ma-comp-set-save-btn:disabled {
					background: #e0e0e0;
					border-color: rgba(118, 118, 118, 0.3);
					color: rgba(16, 16, 16, 0.3);
					cursor: not-allowed;
				}
				@media print {
				.header,
				.navbar,
				.sidebar,
				.button-row {
					display: none !important;
				}

				@page {
					size: letter;
					margin: 0.5in;
				}

				body {
					margin: 0;
					padding: 0;
				}

				.content-container {
					display: block;
				}

				.main-content {
					width: 100% !important;
					margin: 0 !important;
				}
			}
		</style>
		`;
	}

	/**
	 * Called after the view is mounted to the DOM
	 */
	async onMount(): Promise<void> {
		this.attachDownloadListener();
		this.attachAddTransactionListener();

		// Load saved comp set or selected transactions
		await this.loadSavedCompSet();
		await this.loadSelectedTransactions();

		// Attach comp set event listeners
		this.attachCompSetListeners();

		// Add page refresh warning
		window.addEventListener('beforeunload', this.handleBeforeUnload);

		// Clear sessionStorage on page unload
		window.addEventListener('unload', this.handleUnload);
	}

	/**
	 * Called before view is unmounted (when navigating away)
	 */
	async onUnmount(): Promise<void> {
		// Only clear sessionStorage if NOT a saved comp set
		if (!this.storageService.hasSavedMACompSet()) {
			this.storageService.clearMATransactions();
		}

		// Remove event listeners
		window.removeEventListener('beforeunload', this.handleBeforeUnload);
		window.removeEventListener('unload', this.handleUnload);

		// Call parent cleanup
		await super.onUnmount();
	}

	/**
	 * Handle page refresh warning
	 */
	private handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
		const selectedIds = this.storageService.getSelectedMATransactions();
		const hasSavedCompSet = this.storageService.hasSavedMACompSet();

		// Only warn if there are transactions AND no saved comp set
		if (selectedIds.length > 0 && !hasSavedCompSet) {
			// Trigger browser warning (modern browsers show generic message)
			e.preventDefault();
			e.returnValue = ''; // Required for Chrome
			return ''; // For older browsers
		}

		return undefined;
	};

	/**
	 * Handle page unload - clear sessionStorage
	 */
	private handleUnload = (): void => {
		// Only clear if NOT a saved comp set
		if (!this.storageService.hasSavedMACompSet()) {
			this.storageService.clearMATransactions();
		}
	};

	/**
	 * Load selected transactions from session storage
	 */
	private async loadSelectedTransactions(): Promise<void> {
		try {
			const transactionIds = this.storageService.getSelectedMATransactions();

			if (transactionIds.length > 0) {
				this.selectedTransactions = await this.dataService.getMATransactionsByIds(transactionIds);
				this.renderMainTransactionsTable();
			}
		} catch (error) {
			console.error('[MATransactionsView] Failed to load selected transactions:', error);
		}
	}

	/**
	 * Render main transactions table
	 */
	private renderMainTransactionsTable(): void {
		const emptyState = document.getElementById('ma-empty-state');
		const tableContainer = document.getElementById('ma-transactions-table-container');
		const tbody = document.getElementById('ma-transactions-tbody');
		const table = document.getElementById('ma-transactions-main-table');
		const compSetSection = document.getElementById('ma-comp-set-section');

		if (!tbody || !table) return;

		if (this.selectedTransactions.length === 0) {
			if (emptyState) emptyState.style.display = 'block';
			if (tableContainer) tableContainer.style.display = 'none';
			if (compSetSection) compSetSection.style.display = 'none';
			return;
		}

		// Hide empty state, show table and comp set section
		if (emptyState) emptyState.style.display = 'none';
		if (tableContainer) tableContainer.style.display = 'block';
		if (compSetSection) compSetSection.style.display = 'block';

		// Clear tbody and any existing tfoot
		tbody.innerHTML = '';
		const existingTfoot = table.querySelector('tfoot');
		if (existingTfoot) {
			existingTfoot.remove();
		}

		// Render rows
		this.selectedTransactions.forEach((txn, index) => {
			const row = this.createTransactionRow(txn, index, true);
			tbody.appendChild(row);
		});

		// Attach delete icon handlers
		this.attachDeleteIconHandlers();

		// Add summary statistics footer
		const stats = this.calculateSummaryStatistics();
		const footerHTML = this.createSummaryFooter(stats);
		table.insertAdjacentHTML('beforeend', footerHTML);
	}

	/**
	 * Create a transaction table row
	 */
	private createTransactionRow(txn: MATransaction, index: number, isMainTable: boolean): HTMLTableRowElement {
		const tr = document.createElement('tr');
		if (index % 2 === 1) {
			tr.style.backgroundColor = '#f5f5f5';
		}

		// Format announced date from YYYY-MM-DD to MMM-DD-YYYY
		const dateObj = new Date(txn.announcedDate);
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const formattedDate = `${monthNames[dateObj.getMonth()]}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;

		// Format buyers
		const buyersText = txn.buyers.length > 0
			? txn.buyers.map((b: MACompanyEntity) => b.name).join(', ')
			: '-';

		// Format sellers
		const sellersText = txn.sellers.length > 0
			? txn.sellers.map((s: MACompanyEntity) => s.name).join(', ')
			: '-';

		// Format Target TEV
		const targetTevText = (txn.targetTevUsdMm !== null && txn.targetTevUsdMm !== undefined)
			? txn.targetTevUsdMm.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
			: '-';

		// Format size
		const sizeText = (txn.sizeUsdMm !== null && txn.sizeUsdMm !== undefined)
			? txn.sizeUsdMm.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
			: '-';

		// Format TEV ratios
		const tevRevenueText = txn.tevLtmRevenue || '-';
		const tevEbitdaText = txn.tevLtmEbitda || '-';

		if (isMainTable) {
			// Main table with Delete icon and Target TEV column
			tr.innerHTML = `
				<td align="center">
					<img class="ma-delete-icon" src="https://www.capitaliq.com/CIQDotNet/images/delete-icon.png"
						alt="Delete"
						data-transaction-id="${txn.id}"
						onerror="this.style.display='none'; this.insertAdjacentHTML('afterend', '<span style=\\'color:red;cursor:pointer;font-weight:bold;\\' class=\\'ma-delete-icon\\' data-transaction-id=\\'${txn.id}\\'>×</span>');">
				</td>
				<td align="left">${formattedDate}</td>
				<td align="left">${txn.target.name}</td>
				<td align="left">${buyersText}</td>
				<td align="left">${sellersText}</td>
				<td align="right">${targetTevText}</td>
				<td align="right">${sizeText}</td>
				<td align="right">${tevRevenueText}</td>
				<td align="right">${tevEbitdaText}</td>
			`;
		} else {
			// Modal table with Add icon
			const isAlreadyAdded = this.storageService.isMATransactionSelected(txn.id);
			const iconClass = isAlreadyAdded ? 'ma-add-icon disabled' : 'ma-add-icon';
			const iconSrc = isAlreadyAdded
				? 'https://www.capitaliq.com/CIQDotNet/images/add-icon-disabled.png'
				: 'https://www.capitaliq.com/CIQDotNet/images/add-icon.png';

			tr.innerHTML = `
				<td align="center">
					<img class="${iconClass}" src="${iconSrc}"
						alt="Add"
						data-transaction-id="${txn.id}"
						onerror="this.style.display='none'; this.insertAdjacentHTML('afterend', '<span style=\\'color:green;cursor:pointer;font-weight:bold;\\' class=\\'${iconClass}\\' data-transaction-id=\\'${txn.id}\\'>+</span>');">
				</td>
				<td align="left">${formattedDate}</td>
				<td align="left">${txn.target.name}</td>
				<td align="left">${buyersText}</td>
				<td align="left">${sellersText}</td>
				<td align="right">${sizeText}</td>
				<td align="right">${tevRevenueText}</td>
				<td align="right">${tevEbitdaText}</td>
			`;
		}

		return tr;
	}

	/**
	 * Attach download button listener
	 */
	private attachDownloadListener(): void {
		const downloadButton = document.querySelector('.download-button');

		if (downloadButton) {
			this.addEventListener(downloadButton, 'click', () => {
				this.exportToExcel();
			});
		}

		// Set up Print button handler
		const printBtn = document.querySelector('.print-button');
		if (printBtn) {
			this.addEventListener(printBtn, 'click', () => {
				window.print();
			});
		}
	}

	/**
	 * Export M&A transactions table to Excel
	 */
	private exportToExcel(): void {
		// Check if there are transactions to export
		if (this.selectedTransactions.length === 0) {
			alert('No transactions to export. Please add transactions first.');
			return;
		}

		// Extract table data
		const tableData: (string | number)[][] = [];

		// Add headers
		tableData.push([
			'Announced Date',
			'Target',
			'Buyer/Investor',
			'Seller',
			'Target TEV (USD mm)',
			'Size (USD mm)',
			'Implied TEV/LTM Revenue',
			'Implied TEV/LTM EBITDA'
		]);

		// Add transaction rows
		this.selectedTransactions.forEach(txn => {
			// Format announced date from YYYY-MM-DD to MMM-DD-YYYY
			const dateObj = new Date(txn.announcedDate);
			const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			const formattedDate = `${monthNames[dateObj.getMonth()]}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;

			// Format buyers
			const buyersText = txn.buyers.length > 0
				? txn.buyers.map((b: MACompanyEntity) => b.name).join(', ')
				: '-';

			// Format sellers
			const sellersText = txn.sellers.length > 0
				? txn.sellers.map((s: MACompanyEntity) => s.name).join(', ')
				: '-';

			// Format numeric values
			const targetTev = txn.targetTevUsdMm ?? '-';
			const size = txn.sizeUsdMm ?? '-';
			const tevRevenue = txn.tevLtmRevenue || '-';
			const tevEbitda = txn.tevLtmEbitda || '-';

			tableData.push([
				formattedDate,
				txn.target.name,
				buyersText,
				sellersText,
				targetTev,
				size,
				tevRevenue,
				tevEbitda
			]);
		});

		// Add summary statistics
		const stats = this.calculateSummaryStatistics();

		// Add empty row for separation
		tableData.push([]);

		// Add summary rows
		const formatStat = (val: number | null): string | number => {
			if (val == null) return '-';
			return val;
		};

		tableData.push([
			'',
			'',
			'',
			'High',
			formatStat(stats.targetTev.max),
			formatStat(stats.size.max),
			formatStat(stats.tevRevenue.max),
			formatStat(stats.tevEbitda.max)
		]);

		tableData.push([
			'',
			'',
			'',
			'Low',
			formatStat(stats.targetTev.min),
			formatStat(stats.size.min),
			formatStat(stats.tevRevenue.min),
			formatStat(stats.tevEbitda.min)
		]);

		tableData.push([
			'',
			'',
			'',
			'Mean',
			formatStat(stats.targetTev.mean),
			formatStat(stats.size.mean),
			formatStat(stats.tevRevenue.mean),
			formatStat(stats.tevEbitda.mean)
		]);

		tableData.push([
			'',
			'',
			'',
			'Median',
			formatStat(stats.targetTev.median),
			formatStat(stats.size.median),
			formatStat(stats.tevRevenue.median),
			formatStat(stats.tevEbitda.median)
		]);

		// Create workbook and worksheet
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet(tableData);

		// Set column widths
		worksheet['!cols'] = [
			{ wch: 15 },  // Announced Date
			{ wch: 30 },  // Target
			{ wch: 30 },  // Buyer/Investor
			{ wch: 30 },  // Seller
			{ wch: 18 },  // Target TEV
			{ wch: 15 },  // Size
			{ wch: 22 },  // TEV/LTM Revenue
			{ wch: 22 }   // TEV/LTM EBITDA
		];

		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(workbook, worksheet, 'M&A Transactions');

		// Generate filename with current date
		const today = new Date();
		const dateStr = today.toISOString().split('T')[0];
		const filename = `NVIDIA_MA_Transactions_${dateStr}.xlsx`;

		// Download the file
		XLSX.writeFile(workbook, filename);
	}

	/**
	 * Attach Add Transaction button listener
	 */
	private attachAddTransactionListener(): void {
		const addButton = document.getElementById('add-transaction-btn');

		if (addButton) {
			this.addEventListener(addButton, 'click', () => {
				this.openTransactionModal();
			});
		}
	}

	/**
	 * Open the transaction search modal
	 */
	private openTransactionModal(): void {
		// Check if modal already exists
		if (document.getElementById('ma-modal-overlay')) {
			return;
		}

		// Create modal HTML
		const modalHTML = `
			<div class="ma-modal-overlay" id="ma-modal-overlay">
				<div class="ma-modal-container">
					<div class="ma-modal-header">
						<span>Search Transaction</span>
						<button class="ma-modal-close" id="ma-modal-close">&times;</button>
					</div>
					<div class="ma-modal-body">
						<div class="ma-modal-section-title">Add Transactions</div>
						<div class="ma-modal-content">
							<!-- Search By Section -->
							<div class="ma-search-by-section">
								<label class="ma-search-label">Search by:</label>
								<div class="ma-radio-group">
									<label class="ma-radio-option">
										<input type="radio" name="searchType" value="tickers" id="searchTypeTickers" checked>
										<span>Company Tickers</span>
									</label>
									<label class="ma-radio-option">
										<input type="radio" name="searchType" value="name" id="searchTypeName">
										<span>Company Name</span>
									</label>
								</div>
							</div>

							<!-- Target/Buyer Company Input -->
							<div class="ma-company-input-section">
								<label class="ma-company-label">Target/Buyer<br>Company Name:</label>
								<div class="ma-input-group">
									<input type="text" id="companyInput" class="ma-company-input" placeholder="Enter Company Ticker...">
									<button class="ma-go-button" id="searchGoButton">Go</button>
								</div>
							</div>

							<!-- Dual List Section -->
							<div class="ma-dual-list-section">
								<div class="ma-list-container">
									<div class="ma-list-header">Results:</div>
									<select id="resultsList" class="ma-list-select" multiple size="8"></select>
								</div>
								<div class="ma-arrow-buttons">
									<button type="button" class="ma-arrow-btn" id="moveRightSingle">&gt;</button>
									<button type="button" class="ma-arrow-btn" id="moveRightAll">&gt;&gt;</button>
									<button type="button" class="ma-arrow-btn" id="moveLeftAll">&lt;&lt;</button>
									<button type="button" class="ma-arrow-btn" id="moveLeftSingle">&lt;</button>
								</div>
								<div class="ma-list-container">
									<div class="ma-list-header">Selected Entities:</div>
									<select id="selectedList" class="ma-list-select" multiple size="8"></select>
								</div>
							</div>

							<!-- Date Range Section -->
							<div class="ma-date-section">
								<div class="ma-date-row">
									<label class="ma-date-label">From Announced Date:</label>
									<div class="ma-date-input-wrapper">
										<input type="date" id="fromDate" class="ma-date-input">
									</div>
								</div>
								<div class="ma-date-row">
									<label class="ma-date-label">To Announced Date:</label>
									<div class="ma-date-input-wrapper">
										<input type="date" id="toDate" class="ma-date-input">
									</div>
								</div>
							</div>

							<!-- Action Buttons -->
							<div class="ma-actions">
								<button class="ma-view-transactions-btn" id="viewTransactionsBtn" disabled>View Transactions</button>
								<button class="ma-reset-btn" id="resetBtn">Reset</button>
							</div>

							<!-- Search Results Table -->
							<div class="ma-modal-results-table-container" id="ma-modal-results-container" style="display: none;">
								<table class="ma-modal-results-table" cellspacing="0" cellpadding="2" rules="all" bordercolor="#d8dde1" border="1">
									<thead>
										<tr>
											<th align="center" style="width: 5%;">Add</th>
											<th align="left" style="width: 10%;">Announced Date</th>
											<th align="left">Target</th>
											<th align="left" style="width: 17%;">Buyer/Investor</th>
											<th align="left" style="width: 17%;">Seller</th>
											<th align="right" style="width: 6%;">Size(USD mm)</th>
											<th align="right" style="width: 11%;">TEV/LTM Revenue</th>
											<th align="right" style="width: 11%;">TEV/LTM EBITDA</th>
										</tr>
									</thead>
									<tbody id="ma-modal-results-tbody"></tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		`;

		// Append modal to body
		document.body.insertAdjacentHTML('beforeend', modalHTML);

		// Attach handlers
		this.attachModalCloseHandlers();
		this.attachModalFunctionalityHandlers();
	}

	/**
	 * Attach modal close handlers
	 */
	private attachModalCloseHandlers(): void {
		const overlay = document.getElementById('ma-modal-overlay');
		const closeButton = document.getElementById('ma-modal-close');

		if (!overlay) return;

		// Close on X button click
		if (closeButton) {
			closeButton.addEventListener('click', () => {
				this.closeTransactionModal();
			});
		}

		// Close on overlay click (click outside modal)
		overlay.addEventListener('click', (e: Event) => {
			if (e.target === overlay) {
				this.closeTransactionModal();
			}
		});

		// Close on Escape key
		const escapeHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				this.closeTransactionModal();
				document.removeEventListener('keydown', escapeHandler);
			}
		};

		document.addEventListener('keydown', escapeHandler);
	}

	/**
	 * Close the transaction search modal
	 */
	private closeTransactionModal(): void {
		const overlay = document.getElementById('ma-modal-overlay');
		if (overlay) {
			overlay.remove();
		}
	}

	/**
	 * Attach modal functionality handlers
	 */
	private attachModalFunctionalityHandlers(): void {
		const tickersRadio = document.getElementById('searchTypeTickers') as HTMLInputElement;
		const nameRadio = document.getElementById('searchTypeName') as HTMLInputElement;
		const companyInput = document.getElementById('companyInput') as HTMLInputElement;
		const searchGoButton = document.getElementById('searchGoButton') as HTMLButtonElement;
		const resultsList = document.getElementById('resultsList') as HTMLSelectElement;
		const selectedList = document.getElementById('selectedList') as HTMLSelectElement;
		const fromDate = document.getElementById('fromDate') as HTMLInputElement;
		const toDate = document.getElementById('toDate') as HTMLInputElement;
		const viewTransactionsBtn = document.getElementById('viewTransactionsBtn') as HTMLButtonElement;
		const resetBtn = document.getElementById('resetBtn') as HTMLButtonElement;

		// Handle radio button changes for search type
		if (tickersRadio && nameRadio && companyInput) {
			tickersRadio.addEventListener('change', () => {
				if (tickersRadio.checked) {
					companyInput.placeholder = 'Enter Company Ticker...';
				}
			});

			nameRadio.addEventListener('change', () => {
				if (nameRadio.checked) {
					companyInput.placeholder = 'Enter Company Name...';
				}
			});
		}

		// Handle Go button click for search
		if (searchGoButton && companyInput && resultsList) {
			searchGoButton.addEventListener('click', () => {
				void (async () => {
				const searchValue = companyInput.value.trim();
				if (searchValue) {
					try {
						// Read which search type is selected
						const searchType = tickersRadio?.checked ? 'tickers' : 'name';
						const companies = await this.dataService.searchMACompaniesByTickerOrName(searchValue, searchType);

						// Clear results list
						resultsList.innerHTML = '';

						// Add companies to results list
						companies.forEach(company => {
							const option = document.createElement('option');
							option.value = company.ticker;
							// Show fullName by default, or just name if showTickerInFullName is explicitly false
						option.textContent = (company.showTickerInFullName ?? true) ? company.fullName : company.name;
							option.dataset.ticker = company.ticker;
							resultsList.appendChild(option);
						});

					} catch (error) {
						console.error('[MATransactionsView] Search failed:', error);
					}
				}
				})();
			});

			// Handle Enter key in company input to trigger Go button
			companyInput.addEventListener('keydown', (e: KeyboardEvent) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					searchGoButton.click();
				}
			});
		}

		// Handle arrow buttons for moving items
		const moveRightSingle = document.getElementById('moveRightSingle');
		const moveRightAll = document.getElementById('moveRightAll');
		const moveLeftAll = document.getElementById('moveLeftAll');
		const moveLeftSingle = document.getElementById('moveLeftSingle');

		if (moveRightSingle && resultsList && selectedList) {
			moveRightSingle.addEventListener('click', () => {
				this.moveSelectedOptions(resultsList, selectedList);
				this.checkViewTransactionsButtonState();
			});
		}

		if (moveRightAll && resultsList && selectedList) {
			moveRightAll.addEventListener('click', () => {
				this.moveAllOptions(resultsList, selectedList);
				this.checkViewTransactionsButtonState();
			});
		}

		if (moveLeftAll && resultsList && selectedList) {
			moveLeftAll.addEventListener('click', () => {
				this.moveAllOptions(selectedList, resultsList);
				this.checkViewTransactionsButtonState();
			});
		}

		if (moveLeftSingle && resultsList && selectedList) {
			moveLeftSingle.addEventListener('click', () => {
				this.moveSelectedOptions(selectedList, resultsList);
				this.checkViewTransactionsButtonState();
			});
		}

		// Handle date changes
		if (fromDate && toDate) {
			fromDate.addEventListener('change', () => {
				this.checkViewTransactionsButtonState();
			});

			toDate.addEventListener('change', () => {
				this.checkViewTransactionsButtonState();
			});
		}

		// Handle Reset button
		if (resetBtn) {
			resetBtn.addEventListener('click', () => {
				this.resetModalFields();
			});
		}

		// Handle View Transactions button
		if (viewTransactionsBtn) {
			viewTransactionsBtn.addEventListener('click', () => {
				void this.loadAndDisplayTransactions();
			});
		}
	}

	/**
	 * Move selected options from one select to another
	 */
	private moveSelectedOptions(fromSelect: HTMLSelectElement, toSelect: HTMLSelectElement): void {
		const selectedOptions = Array.from(fromSelect.selectedOptions);

		selectedOptions.forEach(option => {
			const newOption = document.createElement('option');
			newOption.value = option.value;
			newOption.textContent = option.textContent;
			toSelect.appendChild(newOption);
			fromSelect.removeChild(option);
		});
	}

	/**
	 * Move all options from one select to another
	 */
	private moveAllOptions(fromSelect: HTMLSelectElement, toSelect: HTMLSelectElement): void {
		const allOptions = Array.from(fromSelect.options);

		allOptions.forEach(option => {
			const newOption = document.createElement('option');
			newOption.value = option.value;
			newOption.textContent = option.textContent;
			toSelect.appendChild(newOption);
			fromSelect.removeChild(option);
		});
	}

	/**
	 * Check if View Transactions button should be enabled
	 */
	private checkViewTransactionsButtonState(): void {
		const selectedList = document.getElementById('selectedList') as HTMLSelectElement;
		const fromDate = document.getElementById('fromDate') as HTMLInputElement;
		const toDate = document.getElementById('toDate') as HTMLInputElement;
		const viewTransactionsBtn = document.getElementById('viewTransactionsBtn') as HTMLButtonElement;

		if (selectedList && fromDate && toDate && viewTransactionsBtn) {
			const hasSelectedEntities = selectedList.options.length > 0;
			const hasFromDate = fromDate.value !== '';
			const hasToDate = toDate.value !== '';

			const shouldEnable = hasSelectedEntities && hasFromDate && hasToDate;
			viewTransactionsBtn.disabled = !shouldEnable;

		}
	}

	/**
	 * Reset all modal fields
	 */
	private resetModalFields(): void {
		// Reset radio buttons
		const tickersRadio = document.getElementById('searchTypeTickers') as HTMLInputElement;
		if (tickersRadio) {
			tickersRadio.checked = true;
		}

		// Reset input field
		const companyInput = document.getElementById('companyInput') as HTMLInputElement;
		if (companyInput) {
			companyInput.value = '';
			companyInput.placeholder = 'Enter Company Ticker...';
		}

		// Clear both lists
		const resultsList = document.getElementById('resultsList') as HTMLSelectElement;
		const selectedList = document.getElementById('selectedList') as HTMLSelectElement;

		if (resultsList) {
			resultsList.innerHTML = '';
		}

		if (selectedList) {
			selectedList.innerHTML = '';
		}

		// Reset date fields
		const fromDate = document.getElementById('fromDate') as HTMLInputElement;
		const toDate = document.getElementById('toDate') as HTMLInputElement;

		if (fromDate) {
			fromDate.value = '';
		}

		if (toDate) {
			toDate.value = '';
		}

		// Disable View Transactions button
		this.checkViewTransactionsButtonState();

	}

	/**
	 * Load and display transactions for selected companies
	 */
	private async loadAndDisplayTransactions(): Promise<void> {
		const selectedList = document.getElementById('selectedList') as HTMLSelectElement;
		const fromDate = document.getElementById('fromDate') as HTMLInputElement;
		const toDate = document.getElementById('toDate') as HTMLInputElement;
		const resultsContainer = document.getElementById('ma-modal-results-container');
		const resultsTbody = document.getElementById('ma-modal-results-tbody');

		if (!selectedList || !fromDate || !toDate || !resultsContainer || !resultsTbody) {
			return;
		}

		// Get selected company tickers
		const selectedTickers = Array.from(selectedList.options).map(opt => opt.value);

		if (selectedTickers.length === 0) {
			return;
		}

		try {
			// Load transactions for all selected companies
			const allTransactions: MATransaction[] = [];

			for (const ticker of selectedTickers) {
				const transactions = await this.dataService.getTransactionsForCompany(
					ticker,
					fromDate.value,
					toDate.value
				);
				allTransactions.push(...transactions);
			}

			// Sort by date descending
			allTransactions.sort((a, b) => b.announcedDate.localeCompare(a.announcedDate));

			// Display results
			resultsContainer.style.display = 'block';
			resultsTbody.innerHTML = '';

			allTransactions.forEach((txn, index) => {
				const row = this.createTransactionRow(txn, index, false);
				resultsTbody.appendChild(row);
			});

			// Attach add icon click handlers
			this.attachAddIconHandlers();

		} catch (error) {
			console.error('[MATransactionsView] Failed to load transactions:', error);
		}
	}

	/**
	 * Attach click handlers to add icons in modal results table
	 */
	private attachAddIconHandlers(): void {
		const addIcons = document.querySelectorAll('#ma-modal-results-tbody .ma-add-icon');

		addIcons.forEach(icon => {
			const htmlIcon = icon as HTMLElement;
			const transactionId = htmlIcon.dataset.transactionId;

			if (!transactionId) {
				console.warn('[MATransactionsView] Icon missing transaction ID:', icon);
				return;
			}

			// Don't attach handler if already added
			if (htmlIcon.classList.contains('disabled')) {
				return;
			}

			htmlIcon.addEventListener('click', () => {
				void this.addTransactionToSelection(transactionId, htmlIcon);
			});
		});
	}

	/**
	 * Add transaction to selection
	 */
	private async addTransactionToSelection(transactionId: string, iconElement: HTMLElement): Promise<void> {
		try {
			// Add to session storage
			this.storageService.addMATransaction(transactionId);

			// Update icon to disabled state
			iconElement.classList.add('disabled');
			const imgElement = iconElement as HTMLImageElement;
			if (imgElement.src) {
				imgElement.src = 'https://www.capitaliq.com/CIQDotNet/images/add-icon-disabled.png';
			}

			// Reload selected transactions
			await this.loadSelectedTransactions();

		} catch (error) {
			console.error('[MATransactionsView] Failed to add transaction:', error);
			console.error('[MATransactionsView] Error details:', error);
		}
	}

	/**
	 * Attach delete icon handlers to main table
	 */
	private attachDeleteIconHandlers(): void {
		const deleteIcons = document.querySelectorAll('#ma-transactions-tbody .ma-delete-icon');

		deleteIcons.forEach(icon => {
			const htmlIcon = icon as HTMLElement;
			const transactionId = htmlIcon.dataset.transactionId;

			if (!transactionId) return;

			htmlIcon.addEventListener('click', () => {
				void this.removeTransactionFromSelection(transactionId);
			});
		});
	}

	/**
	 * Remove transaction from selection
	 */
	private async removeTransactionFromSelection(transactionId: string): Promise<void> {
		try {
			// Remove from session storage
			this.storageService.removeMATransaction(transactionId);

			// Reload selected transactions
			await this.loadSelectedTransactions();

		} catch (error) {
			console.error('[MATransactionsView] Failed to remove transaction:', error);
		}
	}

	/**
	 * Calculate summary statistics for numerical columns
	 * Note: If ANY transaction is missing data for a metric, all stats for that metric will be null
	 */
	private calculateSummaryStatistics() {
		// Target TEV: Only calculate if ALL transactions have this value
		const targetTevValues = this.selectedTransactions.map(t => t.targetTevUsdMm);
		const hasNullTargetTev = targetTevValues.some(v => v == null);
		const targetTevStats = hasNullTargetTev
			? { max: null, median: null, mean: null, min: null }
			: this.calcStats(targetTevValues.filter((v): v is number => typeof v === 'number' && !isNaN(v)));

		// Size: Only calculate if ALL transactions have this value
		const sizeValues = this.selectedTransactions.map(t => t.sizeUsdMm);
		const hasNullSize = sizeValues.some(v => v == null);
		const sizeStats = hasNullSize
			? { max: null, median: null, mean: null, min: null }
			: this.calcStats(sizeValues.filter((v): v is number => typeof v === 'number' && !isNaN(v)));

		// TEV/Revenue: Only calculate if ALL transactions have this value
		const tevRevenueRaw = this.selectedTransactions.map(t => t.tevLtmRevenue);
		const hasNullTevRevenue = tevRevenueRaw.some(v => v == null || v === '');
		const tevRevenueStats = hasNullTevRevenue
			? { max: null, median: null, mean: null, min: null }
			: this.calcStats(tevRevenueRaw.map(v => parseFloat(v!)).filter((v): v is number => !isNaN(v)));

		// TEV/EBITDA: Only calculate if ALL transactions have this value
		const tevEbitdaRaw = this.selectedTransactions.map(t => t.tevLtmEbitda);
		const hasNullTevEbitda = tevEbitdaRaw.some(v => v == null || v === '');
		const tevEbitdaStats = hasNullTevEbitda
			? { max: null, median: null, mean: null, min: null }
			: this.calcStats(tevEbitdaRaw.map(v => parseFloat(v!)).filter((v): v is number => !isNaN(v)));

		return {
			targetTev: targetTevStats,
			size: sizeStats,
			tevRevenue: tevRevenueStats,
			tevEbitda: tevEbitdaStats
		};
	}

	/**
	 * Calculate max, median, mean, min for a set of values
	 */
	private calcStats(values: number[]): { max: number | null; median: number | null; mean: number | null; min: number | null } {
		if (values.length === 0) {
			return { max: null, median: null, mean: null, min: null };
		}

		const sorted = [...values].sort((a, b) => a - b);
		const max = sorted[sorted.length - 1]!;
		const min = sorted[0]!;
		const mean = values.reduce((a, b) => a + b, 0) / values.length;
		const median = sorted.length % 2 === 0
			? (sorted[sorted.length / 2 - 1]! + sorted[sorted.length / 2]!) / 2
			: sorted[Math.floor(sorted.length / 2)]!;

		return { max, median, mean, min };
	}

	/**
	 * Create summary footer HTML
	 */
	private createSummaryFooter(stats: {
		targetTev: { max: number | null; median: number | null; mean: number | null; min: number | null };
		size: { max: number | null; median: number | null; mean: number | null; min: number | null };
		tevRevenue: { max: number | null; median: number | null; mean: number | null; min: number | null };
		tevEbitda: { max: number | null; median: number | null; mean: number | null; min: number | null };
	}): string {
		const formatNum = (val: number | null, isMultiple = false): string => {
			if (val == null) return '-';
			return isMultiple
				? val.toFixed(1) + 'x'
				: val.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
		};

		return `
			<tfoot>
				<tr style="background: #e0e0e0; font-weight: bold; color: #767778;">
					<td colspan="5" align="right" style="padding-right: 12px;">Max</td>
					<td align="right">${formatNum(stats.targetTev.max)}</td>
					<td align="right">${formatNum(stats.size.max)}</td>
					<td align="right">${formatNum(stats.tevRevenue.max, true)}</td>
					<td align="right">${formatNum(stats.tevEbitda.max, true)}</td>
				</tr>
				<tr style="background: #e0e0e0; font-weight: bold; color: #767778;">
					<td colspan="5" align="right" style="padding-right: 12px;">Median</td>
					<td align="right">${formatNum(stats.targetTev.median)}</td>
					<td align="right">${formatNum(stats.size.median)}</td>
					<td align="right">${formatNum(stats.tevRevenue.median, true)}</td>
					<td align="right">${formatNum(stats.tevEbitda.median, true)}</td>
				</tr>
				<tr style="background: #e0e0e0; font-weight: bold; color: #767778;">
					<td colspan="5" align="right" style="padding-right: 12px;">Mean</td>
					<td align="right">${formatNum(stats.targetTev.mean)}</td>
					<td align="right">${formatNum(stats.size.mean)}</td>
					<td align="right">${formatNum(stats.tevRevenue.mean, true)}</td>
					<td align="right">${formatNum(stats.tevEbitda.mean, true)}</td>
				</tr>
				<tr style="background: #e0e0e0; font-weight: bold; color: #767778;">
					<td colspan="5" align="right" style="padding-right: 12px;">Min</td>
					<td align="right">${formatNum(stats.targetTev.min)}</td>
					<td align="right">${formatNum(stats.size.min)}</td>
					<td align="right">${formatNum(stats.tevRevenue.min, true)}</td>
					<td align="right">${formatNum(stats.tevEbitda.min, true)}</td>
				</tr>
			</tfoot>
		`;
	}

	/**
	 * Load saved comp set on mount
	 */
	private async loadSavedCompSet(): Promise<void> {
		const savedCompSet = this.storageService.getSavedMACompSet();
		if (savedCompSet) {

			// Load transactions from saved comp set
			this.selectedTransactions = await this.dataService.getMATransactionsByIds(savedCompSet.transactionIds);

			// Render the table
			this.renderMainTransactionsTable();

			// Populate input field with saved name after DOM is rendered
			setTimeout(() => {
				const input = document.getElementById('comp-set-name-input') as HTMLInputElement;
				if (input) {
					input.value = savedCompSet.name;
				}

				// Enable save button since there's text
				const saveBtn = document.getElementById('comp-set-save-btn') as HTMLButtonElement;
				if (saveBtn) {
					saveBtn.disabled = false;
				}
			}, 0);
		}
	}

	/**
	 * Attach comp set event listeners
	 */
	private attachCompSetListeners(): void {
		const input = document.getElementById('comp-set-name-input') as HTMLInputElement;
		const saveBtn = document.getElementById('comp-set-save-btn') as HTMLButtonElement;
		const restoreBtn = document.getElementById('comp-set-restore-btn') as HTMLButtonElement;

		if (input && saveBtn) {
			// Enable/disable save button based on input
			this.addEventListener(input, 'input', () => {
				saveBtn.disabled = input.value.trim() === '';
			});

			// Save button click handler
			this.addEventListener(saveBtn, 'click', () => {
				this.handleSaveCompSet(input.value.trim());
			});
		}

		if (restoreBtn) {
			// Restore button click handler
			this.addEventListener(restoreBtn, 'click', () => {
				this.handleRestoreCompSet();
			});
		}
	}

	/**
	 * Handle save comp set
	 */
	private handleSaveCompSet(name: string): void {
		if (!name) return;

		const transactionIds = this.storageService.getSelectedMATransactions();

		if (transactionIds.length === 0) {
			console.warn('[MATransactionsView] Cannot save comp set with no transactions');
			return;
		}

		const compSet = {
			name,
			transactionIds,
			savedAt: new Date().toISOString()
		};

		this.storageService.setSavedMACompSet(compSet);


		// Optional: Show brief visual feedback
		const saveBtn = document.getElementById('comp-set-save-btn') as HTMLButtonElement;
		if (saveBtn) {
			const originalText = saveBtn.textContent;
			saveBtn.textContent = 'Saved!';
			setTimeout(() => {
				saveBtn.textContent = originalText;
			}, 1500);
		}
	}

	/**
	 * Handle restore comp set
	 */
	private handleRestoreCompSet(): void {

		// Clear saved comp set
		this.storageService.clearSavedMACompSet();

		// Clear selected transactions
		this.storageService.clearMATransactions();

		// Force page refresh
		window.location.reload();
	}
}
